/**
 * Curriculum — the same screen in two modes, because it is the same tree.
 *
 * At the root domain a platform operator AUTHORS it: add a subject, a unit, a
 * chapter, a subtopic; rename any of them; upload the theory note a student
 * reads on a topic or sub-topic; remove an empty node. Years already in the
 * database can be browsed, but neither this screen nor a school's console opens,
 * activates or moves anyone onto another one. At a school URL — or managing a school as support from
 * the platform console — an admin SELECTS from it: tick the boards, grades,
 * topics and sub-topics the school actually runs. Those are different verbs guarded by
 * different capabilities, and the server enforces the difference — this file
 * only decides which controls to draw.
 *
 * Boards and grades are data too: platform operators manage their permanent
 * codes through the same workspace, using the curriculum-level endpoints.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { gradeLabel } from '@cwy/config/contracts';
import type {
  AcademicYearDTO,
  Board,
  ChapterDTO,
  CurriculumLevel,
  CurriculumSelectionDTO,
  CurriculumSubjectDTO,
  CurriculumTreeDTO,
  Grade,
  SubTopicDTO,
  UnitDTO,
} from '@cwy/config/contracts';
import { Button, Input, StudentPage, StudentPageHeader } from '@/components/ds';
import { FinderColumn, type FinderItem } from '@/components/admin/FinderColumn';
import { AdminBlock, AdminHero } from '@/components/admin/AdminBlocks';
import { QueryState } from '@/components/admin/QueryState';
import { TheoryNotePanel } from '@/components/admin/TheoryNotePanel';
import { useSupportSchool } from '@/components/admin/SupportSchool';
import { useClaims } from '@/hooks/useSession';
import {
  CURRICULUM_LEVELS_KEY,
  levelLabel,
  useCurriculumLevels,
} from '@/hooks/useCurriculumLevels';
import { isFirstParty } from '@/lib/tenant';

const cohortKey = (board: Board, grade: Grade) => `${board}:${grade}`;

export function CurriculumPage() {
  const claims = useClaims();
  const support = useSupportSchool();
  // Authoring is the platform's job at the root domain. The same operator
  // managing a school — at its URL, or as support from the console — is there
  // to choose what THAT school runs, not to edit CWY's syllabus from inside it.
  const authoring = Boolean(claims && claims.platformRole !== 'USER' && isFirstParty && !support);

  const [board, setBoard] = useState<Board>('CBSE');
  const [grade, setGrade] = useState<Grade>('TEN');
  const [yearId, setYearId] = useState<string | null>(null);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [chapterId, setChapterId] = useState<string | null>(null);
  const [subtopicId, setSubtopicId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const years = useQuery({
    queryKey: ['curriculum', 'years'],
    queryFn: () => apiGet<AcademicYearDTO[]>('/curriculum/years'),
  });

  const selection = useQuery({
    queryKey: ['tenant-admin', 'curriculum'],
    queryFn: () => apiGet<CurriculumSelectionDTO>('/tenant-admin/curriculum'),
    enabled: !authoring,
  });

  // Which year's tree is on screen. Authoring: whichever the operator picks,
  // the active one first. Selecting: the SCHOOL's own year, and only that —
  // a school still on last year (or already on next) ticks chapters of its
  // own year, and showing it another year's tree would make Save send ids the
  // server then drops, emptying the school's selection.
  const activeYearId = authoring
    ? (yearId ?? years.data?.find((year) => year.isActive)?.id ?? null)
    : (selection.data?.academicYear.id ?? null);

  const tree = useQuery({
    queryKey: ['curriculum', 'tree', board, grade, activeYearId],
    enabled: authoring || Boolean(activeYearId),
    queryFn: () =>
      apiGet<CurriculumTreeDTO>(
        `/curriculum/tree?board=${board}&grade=${grade}` +
          (activeYearId ? `&academicYearId=${activeYearId}` : ''),
      ),
  });

  const subtopics = useQuery({
    queryKey: ['curriculum', 'subtopics', chapterId],
    queryFn: () => apiGet<SubTopicDTO[]>(`/curriculum/chapters/${chapterId}/subtopics`),
    enabled: Boolean(chapterId),
  });

  // The school's selection, held locally while it is edited and sent as a whole.
  // Seeded from the server the first time it arrives — adjusting state during
  // render rather than from an effect, because an effect that writes would fire
  // twice under StrictMode and is banned here for exactly that reason.
  //
  // Sub-topics are held as SKIPS, the way the server stores them: a ticked topic
  // brings every sub-topic under it except the ones unticked here, including
  // any CWY adds later.
  const [draft, setDraft] = useState<{
    cohorts: Set<string>;
    chapters: Set<string>;
    skipped: Set<string>;
  } | null>(null);
  const [seededFrom, setSeededFrom] = useState<CurriculumSelectionDTO | null>(null);
  if (selection.data && selection.data !== seededFrom) {
    setSeededFrom(selection.data);
    setDraft({
      cohorts: new Set(selection.data.grades.map((row) => cohortKey(row.board, row.grade))),
      chapters: new Set(selection.data.chapterIds),
      skipped: new Set(selection.data.excludedSubtopicIds),
    });
    // Open on a board this school runs. The column lists only those, and an
    // ICSE-only school must not land on CBSE's subjects.
    const runs = selection.data.grades.map((row) => row.board);
    if (runs.length > 0 && !runs.includes(board)) setBoard(runs[0]!);
  }

  const subjects = tree.data?.subjects ?? [];
  const subject = subjects.find((row) => row.id === subjectId) ?? null;
  const unit = subject?.units.find((row) => row.id === unitId) ?? null;

  const chaptersUnder = (node: CurriculumSubjectDTO | UnitDTO): ChapterDTO[] =>
    'units' in node ? node.units.flatMap((each) => each.chapters) : node.chapters;

  const countSelected = (node: CurriculumSubjectDTO | UnitDTO): number =>
    chaptersUnder(node).filter((chapter) => draft?.chapters.has(chapter.id)).length;

  const saveSelection = useMutation({
    mutationFn: () =>
      apiPut('/tenant-admin/curriculum', {
        grades: [...(draft?.cohorts ?? [])].map((key) => {
          const [b, g] = key.split(':');
          return { board: b as Board, grade: g as Grade };
        }),
        chapterIds: [...(draft?.chapters ?? [])],
        excludedSubtopicIds: [...(draft?.skipped ?? [])],
      }),
    onSuccess: () => {
      setSeededFrom(null);
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'curriculum'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'classrooms'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const addNode = useMutation({
    mutationFn: (input: { level: CurriculumLevel; parentId: string; name: string }) =>
      apiPost('/curriculum/nodes', {
        ...input,
        ...(input.level === 'SUBJECT' ? { board, grade } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['curriculum'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const renameNode = useMutation({
    mutationFn: (input: { level: CurriculumLevel; id: string; name: string }) =>
      apiPatch(`/curriculum/nodes/${input.id}`, { level: input.level, name: input.name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['curriculum'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  // Boards and grades: the two levels that are rows of their own, not tree nodes.
  const levels = useCurriculumLevels();
  const addLevel = useMutation({
    mutationFn: (input: { kind: 'BOARD' | 'GRADE'; code: string; label: string }) =>
      apiPost('/curriculum/levels', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CURRICULUM_LEVELS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['curriculum'] });
    },
    onError: (err: Error) => setError(err.message),
  });
  const removeLevel = useMutation({
    mutationFn: (input: { kind: 'BOARD' | 'GRADE'; code: string }) =>
      apiDelete(`/curriculum/levels/${input.kind}/${input.code}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CURRICULUM_LEVELS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['curriculum'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const removeNode = useMutation({
    mutationFn: (input: { level: CurriculumLevel; id: string }) =>
      apiDelete(`/curriculum/nodes/${input.id}?level=${input.level}`),
    onSuccess: (_result, input) => {
      // Whatever was open beneath the removed node no longer exists.
      if (input.level === 'SUBJECT') setSubjectId(null);
      if (input.level === 'SUBJECT' || input.level === 'UNIT') setUnitId(null);
      if (input.level !== 'SUBTOPIC') setChapterId(null);
      setSubtopicId(null);
      void queryClient.invalidateQueries({ queryKey: ['curriculum'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const toggleCohort = (key: string) =>
    setDraft((current) => {
      if (!current) return current;
      const cohorts = new Set(current.cohorts);
      const chapters = new Set(current.chapters);
      if (cohorts.has(key)) {
        cohorts.delete(key);
        // Chapters of a cohort the school no longer runs go with it, or they
        // would be saved and then silently dropped by the server, which filters
        // chapters to offered cohorts.
        //
        // Only the cohort ON SCREEN can be cleared here: `subjects` holds the
        // tree for the selected board and grade and no other, so unticking a
        // cohort from a different column has no chapter list to clear. The
        // server does that half — which is why it filters rather than trusting
        // the payload.
        if (key === cohortKey(board, grade)) {
          for (const chapter of subjects.flatMap(chaptersUnder)) chapters.delete(chapter.id);
        }
      } else {
        cohorts.add(key);
      }
      return { ...current, cohorts, chapters };
    });

  const toggleChapters = (ids: string[]) =>
    setDraft((current) => {
      if (!current) return current;
      const chapters = new Set(current.chapters);
      const allOn = ids.every((id) => chapters.has(id));
      for (const id of ids) {
        if (allOn) chapters.delete(id);
        else chapters.add(id);
      }
      // Ticking a chapter implies running its cohort — otherwise the server
      // drops it on save and the screen appears to forget what was ticked.
      const cohorts = new Set(current.cohorts);
      if (!allOn) cohorts.add(cohortKey(board, grade));
      // Re-ticking the open topic brings all of it back, rather than whatever
      // was skipped before it was unticked.
      const skipped = new Set(current.skipped);
      if (!allOn && chapterId && ids.includes(chapterId)) {
        for (const row of subtopics.data ?? []) skipped.delete(row.id);
      }
      return { cohorts, chapters, skipped };
    });

  /**
   * Tick or untick one sub-topic of the open topic.
   *
   * Ticking one under an unticked topic offers the topic with just that
   * sub-topic; unticking the last one unticks the topic, since a topic offered
   * with nothing in it is not something a student can be shown.
   */
  const toggleSubtopic = (id: string) =>
    setDraft((current) => {
      if (!current || !chapterId) return current;
      const siblings = (subtopics.data ?? []).map((row) => row.id);
      const chapters = new Set(current.chapters);
      const skipped = new Set(current.skipped);
      const cohorts = new Set(current.cohorts);

      if (!chapters.has(chapterId)) {
        chapters.add(chapterId);
        cohorts.add(cohortKey(board, grade));
        for (const sibling of siblings) {
          if (sibling === id) skipped.delete(sibling);
          else skipped.add(sibling);
        }
      } else if (skipped.has(id)) {
        skipped.delete(id);
      } else {
        skipped.add(id);
        if (siblings.every((sibling) => skipped.has(sibling))) {
          chapters.delete(chapterId);
          for (const sibling of siblings) skipped.delete(sibling);
        }
      }
      return { cohorts, chapters, skipped };
    });

  /**
   * Rename handler for one column, or undefined when the caller may not author.
   *
   * The name is asked for and sent; the SLUG never changes, which is what keeps
   * every question filed under this node attached to it.
   */
  const renameIn = (level: CurriculumLevel, items: { id: string; label: string }[]) => {
    if (!authoring) return undefined;
    return (id: string) => {
      const current = items.find((item) => item.id === id)?.label ?? '';
      const next = window.prompt(`Rename “${current}” to:`, current);
      if (next?.trim() && next.trim() !== current) {
        setError('');
        renameNode.mutate({ level, id, name: next.trim() });
      }
    };
  };

  /**
   * Remove handler for one column, or undefined when the caller may not author.
   *
   * The server refuses anything with questions, scaffold content, a paper or a
   * teacher under it, and says what — so this only has to ask whether they mean it.
   */
  const removeIn = (level: CurriculumLevel, items: { id: string; label: string }[]) => {
    if (!authoring) return undefined;
    return (id: string) => {
      const label = items.find((item) => item.id === id)?.label ?? '';
      const below =
        level === 'SUBTOPIC' ? '' : ' and everything under it, in every school that offers it';
      if (window.confirm(`Remove “${label}”${below}? This cannot be undone.`)) {
        setError('');
        removeNode.mutate({ level, id });
      }
    };
  };

  /** Add/remove for the Board and Grade columns — authoring only. */
  const levelFooter = (kind: 'BOARD' | 'GRADE') =>
    authoring ? (
      <AddLevelInline
        kind={kind}
        pending={addLevel.isPending}
        onAdd={(code, label) => {
          setError('');
          addLevel.mutate({ kind, code, label });
        }}
      />
    ) : undefined;
  const levelRemove = (kind: 'BOARD' | 'GRADE') => {
    if (!authoring) return undefined;
    return (code: string) => {
      const row = levels.data?.[kind === 'BOARD' ? 'boards' : 'grades'].find(
        (l) => l.code === code,
      );
      if (row?.inUse) {
        setError(`${code} has subjects or students under it, so it stays.`);
        return;
      }
      if (
        window.confirm(
          `Remove ${kind === 'BOARD' ? 'board' : 'grade'} ${code}? This cannot be undone.`,
        )
      ) {
        setError('');
        removeLevel.mutate({ kind, code });
      }
    };
  };

  const addFooter = (level: CurriculumLevel, parentId: string | null, what: string) => {
    if (!authoring || !parentId) return undefined;
    return (
      <AddInline
        placeholder={`Add ${what}`}
        pending={addNode.isPending}
        onAdd={(name) => {
          setError('');
          addNode.mutate({ level, parentId, name });
        }}
      />
    );
  };

  const selectable = !authoring && draft !== null;
  const dirty =
    selectable &&
    seededFrom !== null &&
    (draft.chapters.size !== seededFrom.chapterIds.length ||
      draft.cohorts.size !== seededFrom.grades.length ||
      seededFrom.chapterIds.some((id) => !draft.chapters.has(id)) ||
      draft.skipped.size !== seededFrom.excludedSubtopicIds.length ||
      seededFrom.excludedSubtopicIds.some((id) => !draft.skipped.has(id)));

  // A school chooses topics, never boards: those are set by CWY (TenantBoard).
  // So outside authoring the Board column lists only the school's own boards —
  // read from that list, not inferred from what is ticked, so a board whose
  // grades were all unticked can still be ticked again, and the server refuses
  // any other.
  const schoolBoards = new Set(seededFrom?.boards ?? []);
  const boardItems: FinderItem[] = (tree.data?.boards ?? [])
    .filter((value) => authoring || seededFrom === null || schoolBoards.has(value))
    .map((value) => {
      const label = levelLabel(levels.data, 'boards', value);
      return { id: value, label: value, meta: label !== value ? label : undefined };
    });

  // Authoring sees every year, with what it holds and who runs it; a school
  // sees its own year only — the one its selection is for.
  const yearItems: FinderItem[] = (years.data ?? [])
    .filter((year) => authoring || year.id === activeYearId)
    .map((year) => ({
      id: year.id,
      label: year.label,
      meta: authoring
        ? [
            year.isActive ? 'Current' : null,
            year.subjectCount === 0 ? 'empty' : null,
            year.schoolCount
              ? `${year.schoolCount} school${year.schoolCount === 1 ? '' : 's'}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        : year.isActive
          ? 'Current'
          : undefined,
    }));
  const shownYear = (years.data ?? []).find((year) => year.id === activeYearId) ?? null;

  const gradeItems: FinderItem[] = (tree.data?.grades ?? []).map((value) => ({
    id: value,
    label: levels.data ? levelLabel(levels.data, 'grades', value) : `Grade ${gradeLabel(value)}`,
    checked: selectable ? draft.cohorts.has(cohortKey(board, value)) : undefined,
  }));

  const subjectItems: FinderItem[] = subjects.map((row) => ({
    id: row.id,
    label: row.name,
    meta: selectable
      ? `${countSelected(row)}/${chaptersUnder(row).length}`
      : `${row.units.length} units`,
    checked: selectable
      ? chaptersUnder(row).length > 0 && countSelected(row) === chaptersUnder(row).length
      : undefined,
    partial: selectable ? countSelected(row) > 0 : undefined,
  }));

  const unitItems: FinderItem[] = (subject?.units ?? []).map((row) => ({
    id: row.id,
    label: row.name,
    meta: selectable
      ? `${countSelected(row)}/${row.chapters.length}`
      : `${row.chapters.length} topics`,
    checked: selectable
      ? row.chapters.length > 0 && countSelected(row) === row.chapters.length
      : undefined,
    partial: selectable ? countSelected(row) > 0 : undefined,
  }));

  const chapterItems: FinderItem[] = (unit?.chapters ?? []).map((row) => ({
    id: row.id,
    label: row.name,
    meta: `${row.subtopicCount}`,
    checked: selectable ? draft.chapters.has(row.id) : undefined,
    // Only the open topic's sub-topics are loaded, so only it can show that
    // some of them are skipped.
    partial:
      selectable && row.id === chapterId
        ? (subtopics.data ?? []).some((sub) => draft.skipped.has(sub.id))
        : undefined,
  }));

  const chapterOffered = Boolean(chapterId && draft?.chapters.has(chapterId));
  const subtopicItems: FinderItem[] = (subtopics.data ?? []).map((row) => ({
    id: row.id,
    label: row.name,
    leaf: true,
    checked: selectable ? chapterOffered && !draft.skipped.has(row.id) : undefined,
  }));
  const allUnits = subjects.flatMap((row) => row.units);
  const allChapters = allUnits.flatMap((row) => row.chapters);
  const path = [
    levelLabel(levels.data, 'boards', board),
    shownYear?.label,
    levels.data ? levelLabel(levels.data, 'grades', grade) : `Grade ${gradeLabel(grade)}`,
    subject?.name,
    unit?.name,
    chapterItems.find((row) => row.id === chapterId)?.label,
    subtopicItems.find((row) => row.id === subtopicId)?.label,
  ].filter((label): label is string => Boolean(label));

  return (
    <StudentPage className={authoring ? '!max-w-none' : ''}>
      {authoring ? (
        <AdminHero
          eyebrow="CWY · Curriculum studio"
          title="Great learning starts here."
          description="Build a clear path from the first subject to the smallest concept. Every school starts with the foundation you create."
        >
          <div className="rounded-card border border-line bg-bin-green-bg p-5 text-bin-green-text">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-fs-11 font-bold uppercase tracking-widest">Academic year</p>
              <span className="rounded-full bg-card px-2.5 py-1 text-fs-10 font-semibold">
                {shownYear?.isActive
                  ? 'Current for new schools'
                  : shownYear
                    ? 'Available year'
                    : 'Loading…'}
              </span>
            </div>
            <p className="mt-3 text-4xl font-bold tracking-tight">{shownYear?.label ?? '—'}</p>
            <p className="mt-2 text-xs leading-relaxed">
              {shownYear
                ? `${shownYear.schoolCount} school${shownYear.schoolCount === 1 ? '' : 's'} running this year.`
                : 'Choose a year to explore its syllabus.'}
            </p>
          </div>
        </AdminHero>
      ) : (
        <StudentPageHeader
          title="Curriculum"
          subtitle="Tick what this school runs, down to the sub-topic. Students only see what is ticked, and classes and teacher subjects read it too."
          action={
            selectable ? (
              <Button
                disabled={!dirty || saveSelection.isPending}
                onClick={() => saveSelection.mutate()}
              >
                {saveSelection.isPending ? 'Saving…' : dirty ? 'Save selection' : 'Saved'}
              </Button>
            ) : undefined
          }
        />
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      <QueryState isPending={tree.isPending || years.isPending} error={tree.error ?? years.error}>
        {authoring && (
          <>
            <section
              aria-label="Curriculum totals for the selected board, grade and year"
              className="grid grid-cols-2 gap-3 xl:grid-cols-4"
            >
              <AdminBlock
                label="Subjects"
                value={subjects.length}
                detail="The starting points for learning"
                tone="GREEN"
              />
              <AdminBlock
                label="Units"
                value={allUnits.length}
                detail="Organised into focused areas"
                tone="PURPLE"
              />
              <AdminBlock
                label="Topics"
                value={allChapters.length}
                detail="The ideas students work through"
                tone="BLUE"
              />
              <AdminBlock
                label="Sub-topics"
                value={allChapters.reduce((sum, row) => sum + row.subtopicCount, 0)}
                detail="Smaller steps towards understanding"
                tone="ORANGE"
              />
            </section>
            <div>
              <h2 className="text-lg font-bold text-forest">Shape your syllabus</h2>
              <p className="mt-1 text-xs text-ink-soft">
                Choose a board, academic year and grade. The totals above reflect this selection.
              </p>
            </div>
          </>
        )}
        <div
          className={
            authoring
              ? 'flex flex-col gap-5'
              : 'overflow-hidden rounded-card border border-line bg-card'
          }
        >
          <div className={authoring ? 'contents' : 'flex h-[520px] overflow-x-auto'}>
            <div className={authoring ? 'grid gap-3 md:grid-cols-3' : 'contents'}>
              <FinderColumn
                title="Board"
                tone={authoring ? 'GREEN' : undefined}
                step={authoring ? '01' : undefined}
                className={authoring ? 'h-[240px]' : ''}
                items={boardItems}
                selectedId={board}
                footer={levelFooter('BOARD')}
                onRemove={levelRemove('BOARD')}
                onSelect={(id) => {
                  setBoard(id as Board);
                  setSubjectId(null);
                  setUnitId(null);
                  setChapterId(null);
                }}
              />
              <FinderColumn
                title="Academic year"
                tone={authoring ? 'PURPLE' : undefined}
                step={authoring ? '02' : undefined}
                className={authoring ? 'h-[240px]' : ''}
                items={yearItems}
                selectedId={activeYearId}
                onSelect={(id) => {
                  setYearId(id);
                  setSubjectId(null);
                  setUnitId(null);
                  setChapterId(null);
                }}
              />
              <FinderColumn
                title="Grade"
                tone={authoring ? 'BLUE' : undefined}
                step={authoring ? '03' : undefined}
                className={authoring ? 'h-[240px]' : ''}
                items={gradeItems}
                selectedId={grade}
                footer={levelFooter('GRADE')}
                onRemove={levelRemove('GRADE')}
                onSelect={(id) => {
                  setGrade(id as Grade);
                  setSubjectId(null);
                  setUnitId(null);
                  setChapterId(null);
                }}
                onToggle={
                  selectable ? (id) => toggleCohort(cohortKey(board, id as Grade)) : undefined
                }
              />
            </div>
            {authoring && (
              <div className="rounded-card border border-line bg-card px-4 py-3">
                <p className="mb-2 text-fs-10 font-bold uppercase tracking-widest text-ink-soft">
                  Your current path
                </p>
                <ol
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold text-forest"
                  aria-label="Selected curriculum path"
                >
                  {path.map((label, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {index > 0 && (
                        <span aria-hidden="true" className="text-ink-faint">
                          ›
                        </span>
                      )}
                      {label}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className={authoring ? 'grid gap-3 sm:grid-cols-2 xl:grid-cols-4' : 'contents'}>
              <FinderColumn
                title="Subject"
                tone={authoring ? 'PURPLE' : undefined}
                step={authoring ? '04' : undefined}
                className={authoring ? 'h-[420px]' : ''}
                items={subjectItems}
                selectedId={subjectId}
                onSelect={(id) => {
                  setSubjectId(id);
                  setUnitId(null);
                  setChapterId(null);
                }}
                onToggle={
                  selectable
                    ? (id) => {
                        const node = subjects.find((row) => row.id === id);
                        if (node) toggleChapters(chaptersUnder(node).map((each) => each.id));
                      }
                    : undefined
                }
                onRename={renameIn('SUBJECT', subjectItems)}
                onRemove={removeIn('SUBJECT', subjectItems)}
                footer={addFooter('SUBJECT', activeYearId, 'a subject')}
                emptyLabel="No subjects for this board and grade"
              />
              <FinderColumn
                title="Unit"
                tone={authoring ? 'BLUE' : undefined}
                step={authoring ? '05' : undefined}
                className={authoring ? 'h-[420px]' : ''}
                items={unitItems}
                selectedId={unitId}
                onSelect={(id) => {
                  setUnitId(id);
                  setChapterId(null);
                }}
                onToggle={
                  selectable
                    ? (id) => {
                        const node = subject?.units.find((row) => row.id === id);
                        if (node) toggleChapters(node.chapters.map((each) => each.id));
                      }
                    : undefined
                }
                onRename={renameIn('UNIT', unitItems)}
                onRemove={removeIn('UNIT', unitItems)}
                footer={addFooter('UNIT', subjectId, 'a unit')}
                emptyLabel={subject ? 'No units yet' : 'Choose a subject'}
              />
              <FinderColumn
                title="Topic"
                tone={authoring ? 'ORANGE' : undefined}
                step={authoring ? '06' : undefined}
                className={authoring ? 'h-[420px]' : ''}
                items={chapterItems}
                selectedId={chapterId}
                onSelect={setChapterId}
                onToggle={selectable ? (id) => toggleChapters([id]) : undefined}
                onRename={renameIn('CHAPTER', chapterItems)}
                onRemove={removeIn('CHAPTER', chapterItems)}
                footer={addFooter('CHAPTER', unitId, 'a topic')}
                emptyLabel={unit ? 'No topics yet' : 'Choose a unit'}
              />
              <FinderColumn
                title="Sub-topic"
                tone={authoring ? 'PINK' : undefined}
                step={authoring ? '07' : undefined}
                className={authoring ? 'h-[420px]' : ''}
                items={subtopicItems}
                selectedId={subtopicId}
                onSelect={setSubtopicId}
                onToggle={selectable ? toggleSubtopic : undefined}
                onRename={renameIn('SUBTOPIC', subtopicItems)}
                onRemove={removeIn('SUBTOPIC', subtopicItems)}
                footer={addFooter('SUBTOPIC', chapterId, 'a sub-topic')}
                emptyLabel={chapterId ? 'No sub-topics yet' : 'Choose a topic'}
              />
            </div>
          </div>
        </div>

        {authoring && (
          <p className="text-xs text-ink-soft">
            Renaming changes the label only. The address a question is filed under never moves, so
            content stays attached. A topic you add here reaches every school on this board at once.
            Only something with no questions, scaffold content, papers or teachers under it can be
            removed.
          </p>
        )}

        {/* The note for the deepest thing selected: a sub-topic's own, else its
            topic's. A stale sub-topic id from another topic matches nothing in
            the list, so it falls back to the topic rather than showing a note
            for a node that is no longer on screen. */}
        {authoring &&
          (() => {
            const sub = subtopicItems.find((item) => item.id === subtopicId);
            const chapter = chapterItems.find((item) => item.id === chapterId);
            if (sub) {
              return (
                <TheoryNotePanel
                  key={sub.id}
                  level="SUBTOPIC"
                  nodeId={sub.id}
                  nodeName={sub.label}
                />
              );
            }
            if (chapter) {
              return (
                <TheoryNotePanel
                  key={chapter.id}
                  level="CHAPTER"
                  nodeId={chapter.id}
                  nodeName={chapter.label}
                />
              );
            }
            return null;
          })()}
      </QueryState>
    </StudentPage>
  );
}

/** The "+" at the foot of an authoring column. */
/**
 * A board or grade needs a code and a label. The code is permanent — every
 * curriculum key is built from it — so the form says so, and suggests the
 * `G<n>` form for a numbered grade, which keys read as just the number.
 */
function AddLevelInline({
  kind,
  pending,
  onAdd,
}: {
  kind: 'BOARD' | 'GRADE';
  pending: boolean;
  onAdd: (code: string, label: string) => void;
}) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [expanded, setExpanded] = useState(false);
  const ready = code.trim() && label.trim();
  if (!expanded) {
    return (
      <Button size="sm" variant="ghost" className="w-full" onClick={() => setExpanded(true)}>
        + Add a {kind === 'BOARD' ? 'board' : 'grade'}
      </Button>
    );
  }
  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        onAdd(code.trim().toUpperCase(), label.trim());
        setCode('');
        setLabel('');
      }}
    >
      <Input
        controlSize="sm"
        value={code}
        maxLength={24}
        placeholder={kind === 'BOARD' ? 'Code, e.g. IB' : 'Code, e.g. G9'}
        aria-label={kind === 'BOARD' ? 'Board code' : 'Grade code'}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
      />
      <div className="flex gap-1.5">
        <Input
          controlSize="sm"
          value={label}
          maxLength={60}
          placeholder={
            kind === 'BOARD' ? 'Name, e.g. International Baccalaureate' : 'Name, e.g. Grade 9'
          }
          aria-label={kind === 'BOARD' ? 'Board name' : 'Grade name'}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Button type="submit" size="sm" variant="secondary" disabled={pending || !ready}>
          +
        </Button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-fs-11 text-ink-soft">The code is permanent once added.</p>
        <button
          type="button"
          className="text-fs-11 font-semibold text-ink-soft underline"
          onClick={() => setExpanded(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddInline({
  placeholder,
  pending,
  onAdd,
}: {
  placeholder: string;
  pending: boolean;
  onAdd: (name: string) => void;
}) {
  const [value, setValue] = useState('');
  return (
    <form
      className="flex gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (value.trim()) {
          onAdd(value.trim());
          setValue('');
        }
      }}
    >
      <Input
        controlSize="sm"
        value={value}
        maxLength={160}
        placeholder={placeholder}
        onChange={(event) => setValue(event.target.value)}
      />
      <Button type="submit" size="sm" variant="secondary" disabled={pending || !value.trim()}>
        +
      </Button>
    </form>
  );
}
